import React, { useEffect, useMemo, useRef } from 'react';
import echarts from 'echarts/lib/echarts';
import 'echarts/lib/chart/line';
import 'echarts/lib/chart/bar';
import 'echarts/lib/chart/tree';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/markArea';
import ReactEchartsCore from 'echarts-for-react/lib/core';
import { IVitalDataSetting, TVitalRecordType } from '../../../../types/life-id';
import { useSelector } from 'react-redux';
import TStore from '../../../../types/store';
import {
  axisOps,
  getDataSeries,
  getExtraOptions,
  getOptionsFromBarChartConfig,
  getYRange,
  tooltipFormatter,
} from '../../../../lib/utils/graphs-utils';
import { getServerKey } from './utils/get-server-key';
import { TTimelineRecord } from '../../../../types/vital-data';
import { TGenderType } from '@hilbi/web-common/types/common';

interface ChartProps {
  data: { date: string; value: string; description: string }[];
  type: TVitalRecordType;
  legends: { color: string; text: string }[];
  timeLineRecord: TTimelineRecord;
}

const getOps = (
  data: any,
  legends: any,
  yRange: { min: number; max: number },
  settings: IVitalDataSetting,
  isMultiLine: boolean,
  timeLineRecord?: TTimelineRecord,
) => {
  const { markArea, minAvg, maxAvg } = getExtraOptions(
    settings,
    isMultiLine,
    timeLineRecord?.values,
  );

  return {
    grid: {
      bottom: 40,
      top: 10,
      left: 15,
      right: 0,
      containLabel: true,
      borderColor: 'rgba(255,255,255,0.1)',
    },
    tooltip: {
      show: true,
      backgroundColor: 'rgba(51, 61, 108, 0.9)',
      borderWidth: 0,
      formatter: tooltipFormatter,
      trigger: 'axis',
      axisPointer: {
        type: 'line',
      },
    },
    xAxis: [
      {
        ...axisOps,
        type: 'time',
        axisLabel: {
          color: 'rgba(255,255,255,.4)',
          fontSize: 10,
          margin: 8,
          formatter: (value: any) => {
            const day = new Date(value).getDate();
            const month = new Date(value).getMonth();
            return `${day}.${month + 1}.`;
          },
        },
        // axisPointer: {
        //   snap: true,
        //   lineStyle: {
        //     color: 'rgba(255,255,255,0.1)',
        //     opacity: 0.5,
        //     width: 2,
        //   },
        //   handle: {
        //     show: true,
        //     color: theme.colors.primary,
        //     size: 28,
        //     shadowColor: 'transparent',
        //   },
        //   label: {
        //     show: false,
        //   },
        // },
      },
      {
        show: true,
        silent: true,
        type: 'time',
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        splitLine: {
          show: false,
        },
      },
    ],
    yAxis: {
      ...axisOps,
      ...yRange,
      min: minAvg,
      max: maxAvg,
      position: 'right',
      axisLabel: {
        fontSize: 12,
        fontWeight: 'bolder',
        color: '#fefefe',
        align: 'right',
        margin: 32,
      },
    },
    series:
      data.length > 0
        ? data.map((d: any, i: any) => getDataSeries(d, i, legends[i], markArea))
        : getDataSeries(data, legends[0]),
  };
};

const Chart: React.FC<ChartProps> = ({ data, type, legends, timeLineRecord }) => {
  let chartData = [data.map((item) => [item.date, item.value])];
  const vitalDataRanges = useSelector((state: TStore) => state.lifeId.vitalListDataSettings);

  const gender = useSelector(
    (state: TStore) => state.patients.patientDetail?.profile.sex,
  ) as TGenderType;

  const yRange = useMemo(() => {
    if (!vitalDataRanges) return { min: 0, max: 0 };
    return getYRange(
      data.map((item) => Number(item.value)),
      vitalDataRanges[getServerKey(type === 'BLOOD_PRESSURE' ? 'DIASTOLIC' : type)].range,
    );
  }, [vitalDataRanges, type, data]);

  if (type === 'BLOOD_PRESSURE') {
    chartData = [
      data.map((item) => [item.date, item.value.split(' / ')[0]]),
      data.map((item) => [item.date, item.value.split(' / ')[1]]),
    ];

    const values = [];
    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      values.push(item.value.split(' / ')[0]);
      values.push(item.value.split(' / ')[1]);
    }
  }

  const settings = useMemo(() => {
    // @ts-ignore
    return vitalDataRanges[getServerKey(type === 'BLOOD_PRESSURE' ? 'DIASTOLIC' : type)];
  }, [type, vitalDataRanges]);

  const options = useMemo(() => {
    if (type === 'BMI' || type === 'WAIST') {
      return getOptionsFromBarChartConfig(chartData, legends, yRange, type, gender, timeLineRecord);
    }
    return getOps(chartData, legends, yRange, settings, type === 'BLOOD_PRESSURE', timeLineRecord);
  }, [chartData, legends, yRange, timeLineRecord, settings, gender]);

  const chartRef = useRef<any>(null);
  useEffect(() => {
    //@ts-ignore
    chartRef.current?.getEchartsInstance().setOption(options);
  }, [options, chartRef]);

  return (
    <div>
      <ReactEchartsCore
        echarts={echarts}
        // @ts-ignore
        option={options}
        style={{ height: 200 }}
        ref={chartRef}
      />
    </div>
  );
};

export default Chart;
